import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Tabs, Splitter } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import ProblemDescription from '../../components/Problem/ProblemDescription';
import TestCase from '../../components/Problem/TestCase';
import OutPut from '../../components/Problem/OutPut';
import InterviewEditor from './InterviewEditor';
import { ResultStatus, LogoStatus } from '../../const';
import styles from '../Problem/ProblemContainer.scss';

export const selectedTab = {
  problemStatement: 'Problem Statement',
  customInput: 'Custom Input',
  results: 'Results',
  success: 'Success',
};

const DEFAULT_LANGUAGE = 'python3';
const { NotAttempted, Accepted, Unknown, TimeLimit, WrongAnswer, CompilerError, RuntimeError } =
  LogoStatus;

const { Success, Error, Fail } = ResultStatus;

function IndividualProblem(props) {
  const dispatch = useDispatch();
  const editorRef = useRef(null);

  const [activeTab, setActiveTab] = useState(selectedTab.problemStatement);
  const [stdin, setStdin] = useState('');
  const { editorExpand, toggleExpand, problemId: propProblemId, onDriverCodeModalToggle } = props;
  const [stdout, setStdout] = useState('');
  const [stderr, setStderr] = useState('');
  const [verdict, setVerdict] = useState('');
  const [activeTemplate, setActiveTemplate] = useState(null);

  const {
    last_editor_data,
    results,
    isLoadingResults,
    customInputTracking,
    currentLanguageforProblems,
  } = useSelector(state => state.events);

  const {
    id = '',
    interview = '',
    points = 0,
    problem_details: {
      id: problemId = '',
      description = '',
      difficulty = '',
      limit_memory = '',
      limit_time = '',
      points: problemPoints = 0,
      publication_status = '',
      system_problem = false,
      tags = [],
      templates = [],
      testcases = [],
      title = '',
    } = {},
    problem_id = '',
  } = props.question || {};

  const totalProbs = props?.totalProbs || 0;
  const attemptedCount = props?.attemptedCount || 0;
  const effectiveProblemId = problemId || problem_id;

  // Check if THIS specific problem is expanded
  const isExpanded = editorExpand === effectiveProblemId;

  const storedProblem = useMemo(() => {
    return currentLanguageforProblems.find(item => item.problem_id === effectiveProblemId);
  }, [currentLanguageforProblems, effectiveProblemId]);

  const selectedLanguage = useMemo(() => {
    return storedProblem?.language || DEFAULT_LANGUAGE;
  }, [currentLanguageforProblems]);

  const findTemplateByLanguage = useCallback(
    language => {
      return templates.find(t => t.language === language);
    },
    [templates],
  );

  const code = useMemo(() => {
    if (!templates?.length) return;
    const codeToUse = storedProblem?.code;
    const template = findTemplateByLanguage(selectedLanguage) || templates[0];
    setActiveTemplate(template);
    const initialCode = template?.body || codeToUse || '';
    return initialCode;
  }, [currentLanguageforProblems, selectedLanguage, templates]);

  // Handle custom input results
  useEffect(() => {
    if (customInputTracking && results?.length > 0) {
      const [res] = results;
      setStdout(res.stdout || '');
      setStderr(res.stderr || '');
      setVerdict({ name: 'OK', type: 'success' });
      setActiveTab(selectedTab.customInput);
    } else if (results?.length > 0) {
      setActiveTab(selectedTab.results);
    }
  }, [customInputTracking, results, dispatch]);

  useEffect(() => {
    setActiveTab(selectedTab.problemStatement);
  }, [effectiveProblemId]);

  // Memoized structure mapping for test cases
  const structuredTestCases = useMemo(() => {
    if (!testcases?.length) return [];
    const base = testcases.map(obj => ({ ...obj, logo: NotAttempted }));

    if (!results?.length) return base;

    return base.map(tc => {
      const match = results.find(r => r.testcase_id === tc.id);
      if (!match) return tc;
      const { status, runtime_error, compiler_error, tle_error } = match;
      if (status === Success) return { ...tc, logo: Accepted };
      if (status === Error || status === Fail) return { ...tc, logo: WrongAnswer };
      if (runtime_error) return { ...tc, logo: RuntimeError };
      if (compiler_error) return { ...tc, logo: CompilerError };
      if (tle_error) return { ...tc, logo: TimeLimit };
      return { ...tc, logo: Unknown };
    });
  }, [testcases, results]);

  const submitCode = useCallback(
    (args = { type: 'regular' }) => {
      props.submitCode(code, selectedLanguage, effectiveProblemId, args);
    },
    [props, code, selectedLanguage, effectiveProblemId],
  );

  // Callbacks to prevent re-renders
  const handleTestCode = useCallback(() => {
    submitCode({
      type: 'custom_input',
      ci_stdin: stdin,
    });
  }, [stdin]);

  const handleSwitchLanguage = newLanguage => {
    const template = findTemplateByLanguage(newLanguage);
    if (!template) return;
    setActiveTemplate(template);
    props.switchLanguage?.(newLanguage, effectiveProblemId);
  };

  const updateCode = useCallback(
    newCode => {
      props.onCodeInEditorChange?.(newCode, selectedLanguage, effectiveProblemId);
    },
    [props, selectedLanguage, effectiveProblemId],
  );

  const tabItems = useMemo(
    () => [
      {
        label: selectedTab.problemStatement,
        key: selectedTab.problemStatement,
        children: (
          <ProblemDescription
            problem={{
              id: effectiveProblemId,
              title,
              description,
              difficulty,
              points: problemPoints || points,
              tags,
              limit_memory,
              limit_time,
              publication_status,
              system_problem,
              templates,
              testcases,
            }}
          />
        ),
      },
      {
        label: selectedTab.customInput,
        key: selectedTab.customInput,
        children: (
          <TestCase
            key={stdout}
            stdout={stdout}
            stderr={stderr}
            stdin={stdin}
            verdict={verdict}
            testcases={testcases}
            setInput={setStdin}
            testCode={handleTestCode}
            isLoading={isLoadingResults && customInputTracking?.problem_id === effectiveProblemId}
          />
        ),
      },
      {
        label: selectedTab.results,
        key: selectedTab.results,
        children: (
          <OutPut
            testcases={structuredTestCases}
            leftCount={Math.max(0, totalProbs - attemptedCount)}
            probStatus={props.question?.status || ''}
            isLoading={isLoadingResults && customInputTracking?.problem_id === effectiveProblemId}
          />
        ),
      },
    ],
    [
      effectiveProblemId,
      title,
      description,
      difficulty,
      problemPoints,
      points,
      tags,
      limit_memory,
      limit_time,
      publication_status,
      system_problem,
      templates,
      testcases,
      stdout,
      stderr,
      stdin,
      verdict,
      handleTestCode,
      structuredTestCases,
      totalProbs,
      attemptedCount,
      props.question,
      isLoadingResults,
    ],
  );

  return (
    <div className={styles['l-problem-cont']}>
      <Splitter
        size={0}
        style={{ height: '100%' }}
      >
        <Splitter.Panel
          size={isExpanded ? '0%' : '35%'}
          min="0%"
          style={{ padding: isExpanded ? '0' : '16px 8px 16px 16px' }}
        >
          <div
            className={styles['c-problem-def-results']}
            style={{
              background: 'white',
              borderRadius: '8px',
              height: '100%',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              overflow: 'hidden',
            }}
          >
            <style>
              {`
                .ant-tabs-tab-btn {
                  font-family: 'Roboto' !important;
                  font-size: 14px !important;
                  line-height: 22px !important;
                  letter-spacing: 0% !important;
                }
              `}
            </style>
            <Tabs
              activeKey={activeTab}
              tabBarExtraContent={
                <span
                  style={{
                    color: 'rgba(0,0,0,0.88)',
                    fontWeight: 500,
                    paddingRight: '24px',
                    fontFamily: 'Roboto',
                    fontSize: '14px',
                    lineHeight: '22px',
                    letterSpacing: '0%',
                    textAlign: 'left',
                    textTransform: 'none',
                    verticalAlign: 'middle',
                  }}
                >
                  {points} Points
                </span>
              }
              onChange={key => setActiveTab(key)}
              items={tabItems}
            />
          </div>
        </Splitter.Panel>
        <Splitter.Panel
          size={isExpanded ? '100%' : '50%'}
          min="20%"
          style={{ padding: '16px 16px 16px 8px' }}
        >
          <div
            style={{
              height: '100%',
              background: '#1e1e1e', // Dark background for editor card
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              overflow: 'hidden',
            }}
          >
            <InterviewEditor
              templates={templates}
              selectedLanguage={selectedLanguage}
              code={code}
              activeTemplate={activeTemplate}
              editorExpand={isExpanded}
              switchLanguage={handleSwitchLanguage}
              toggleExpand={() => toggleExpand(effectiveProblemId)}
              updateCode={updateCode}
              submitCode={submitCode}
              disabled={false}
              interview_id={interview}
              problem_id={effectiveProblemId}
              lang={selectedLanguage}
              cursor={null}
              editorRef={editorRef}
              onDriverCodeModalToggle={onDriverCodeModalToggle}
            />
          </div>
        </Splitter.Panel>
      </Splitter>
    </div>
  );
}

export default IndividualProblem;
